/**
 * Long press, for panels.
 *
 * The world handles its own gestures, but the interface is React, and a held
 * finger has to open the same menu a right click does. Browsers cannot be relied
 * on for that: some fire a contextmenu event after a long press, some show their
 * own selection callout instead, and a Discord Activity is whichever browser the
 * player happens to have. So it is measured here rather than asked for.
 */

import { useCallback, useRef } from 'react';

/** How long a finger has to rest before it counts as a hold. */
const HOLD_MS = 420;

/** Movement past this is a scroll or a drag, not a hold, in pixels. */
const SLOP = 10;

export interface LongPressHandlers {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerCancel: () => void;
  /** True when the last press turned into a hold, so the tap after it is ignored. */
  consumed: () => boolean;
}

export function useLongPress(open: (x: number, y: number) => void): LongPressHandlers {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      // A mouse already has a button for this.
      if (event.pointerType !== 'touch') return;

      fired.current = false;
      start.current = { x: event.clientX, y: event.clientY };
      const point = { x: event.clientX, y: event.clientY };

      timer.current = window.setTimeout(() => {
        timer.current = null;
        fired.current = true;
        open(point.x, point.y);
      }, HOLD_MS);
    },
    [open],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const from = start.current;
      if (!from) return;
      if (Math.hypot(event.clientX - from.x, event.clientY - from.y) > SLOP) cancel();
    },
    [cancel],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    consumed: () => fired.current,
  };
}
